// src/pages/WatchlistChart/WatchlistChart.jsx
// Route: /watchlist/:symbol

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { chartService } from '../../services/chartService';
import { watchlistService } from '../../services/watchlistService';
import { useCurrency } from '../../context/CurrencyContext';
import './WatchlistChart.css';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const INTERVALS = [
  { value: '1min',   label: '1M'  },
  { value: '5min',   label: '5M'  },
  { value: '15min',  label: '15M' },
  { value: '1hr',    label: '1H'  },
  { value: '24hr',   label: '1D'  },
  { value: '1month', label: '1Mo' },
];

// Named colour map — used both for bar colours and legend dots
const COLOR = {
  green:  '#15A759',
  yellow: '#f59e0b',
  orange: '#f97316',
  red:    '#dc3545',
};

const resolveColor = (pct) => {
  if (pct >= 1)  return COLOR.green;
  if (pct >= 0)  return COLOR.yellow;
  if (pct >= -1) return COLOR.orange;
  return COLOR.red;
};

const LEGEND_ITEMS = [
  { colorKey: 'green',  label: '≥ +1%  (Strong rise)'  },
  { colorKey: 'yellow', label: '0% to +1%  (Slight rise)'  },
  { colorKey: 'orange', label: '-1% to 0%  (Slight fall)'  },
  { colorKey: 'red',    label: '≤ -1%  (Strong fall)'   },
];

// OHLC stat definitions — order controls display order
const STAT_KEYS = [
  { key: 'open',   label: 'Open'   },
  { key: 'close',  label: 'Close'  },
  { key: 'high',   label: 'High'   },
  { key: 'low',    label: 'Low'    },
  { key: 'median', label: 'Median' },
];

// ── Inline SVG visuals for each stat tile ────────────────────────────────────

const HighChart = () => (
  <svg width="64" height="36" viewBox="0 0 64 36" fill="none" xmlns="http://www.w3.org/2000/svg">
    <polyline
      points="4,30 18,22 32,14 46,8 60,3"
      stroke="#15A759"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    />
    <polygon
      points="4,30 18,22 32,14 46,8 60,3 60,33 4,33"
      fill="#15A759"
      fillOpacity="0.08"
    />
    <circle cx="60" cy="3" r="3" fill="#15A759" />
  </svg>
);

const LowChart = () => (
  <svg width="64" height="36" viewBox="0 0 64 36" fill="none" xmlns="http://www.w3.org/2000/svg">
    <polyline
      points="4,6 18,12 32,18 46,24 60,31"
      stroke="#dc3545"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    />
    <polygon
      points="4,6 18,12 32,18 46,24 60,31 60,3 4,3"
      fill="#dc3545"
      fillOpacity="0.08"
    />
    <circle cx="60" cy="31" r="3" fill="#dc3545" />
  </svg>
);

const MedianChart = () => (
  <svg width="64" height="36" viewBox="0 0 64 36" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="medianGrad" x1="0" y1="0" x2="64" y2="0" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stopColor="#15A759" />
        <stop offset="100%" stopColor="#dc3545" />
      </linearGradient>
    </defs>
    <polyline
      points="4,18 16,10 32,18 48,26 60,18"
      stroke="url(#medianGrad)"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    />
    <circle cx="32" cy="18" r="3" fill="#f59e0b" />
  </svg>
);

const OpenIcon = () => (
  <svg width="44" height="36" viewBox="0 0 44 36" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M6,26 A16,16 0 0,1 38,26"
      stroke="#f59e0b"
      strokeWidth="2.5"
      strokeLinecap="round"
      fill="none"
    />
    <line x1="2" y1="26" x2="42" y2="26" stroke="#f59e0b" strokeWidth="1.5" strokeLinecap="round" opacity="0.4" />
    <line x1="22" y1="6"  x2="22" y2="2"  stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" />
    <line x1="8"  y1="12" x2="5"  y2="9"  stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" />
    <line x1="36" y1="12" x2="39" y2="9"  stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" />
    <line x1="3"  y1="20" x2="0"  y2="20" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" />
    <line x1="41" y1="20" x2="44" y2="20" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const CloseIcon = () => (
  <svg width="44" height="36" viewBox="0 0 44 36" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M6,16 A16,16 0 0,0 38,16"
      stroke="#9b5de5"
      strokeWidth="2.5"
      strokeLinecap="round"
      fill="none"
    />
    <line x1="2" y1="16" x2="42" y2="16" stroke="#9b5de5" strokeWidth="1.5" strokeLinecap="round" opacity="0.4" />
    <line x1="22" y1="30" x2="22" y2="34" stroke="#9b5de5" strokeWidth="2" strokeLinecap="round" opacity="0.5" />
    <line x1="8"  y1="24" x2="5"  y2="27" stroke="#9b5de5" strokeWidth="2" strokeLinecap="round" opacity="0.5" />
    <line x1="36" y1="24" x2="39" y2="27" stroke="#9b5de5" strokeWidth="2" strokeLinecap="round" opacity="0.5" />
    <line x1="3"  y1="16" x2="0"  y2="16" stroke="#9b5de5" strokeWidth="2" strokeLinecap="round" opacity="0.3" />
    <line x1="41" y1="16" x2="44" y2="16" stroke="#9b5de5" strokeWidth="2" strokeLinecap="round" opacity="0.3" />
  </svg>
);

const STAT_VISUAL = {
  high:   <HighChart />,
  low:    <LowChart />,
  median: <MedianChart />,
  open:   <OpenIcon />,
  close:  <CloseIcon />,
};

const WatchlistChart = () => {
  const { symbol } = useParams();
  const navigate   = useNavigate();

  const [selectedInterval, setSelectedInterval] = useState('1hr');
  const [chartData, setChartData]               = useState(null);
  const [currentPrice, setCurrentPrice]         = useState(null);
  const [apiCurrency, setApiCurrency]           = useState('NGN');
  const [chartLoading, setChartLoading]         = useState(true);
  const [chartError, setChartError]             = useState(null);
  const [watched, setWatched]                   = useState(true);
  const [removing, setRemoving]                 = useState(false);

  // ── OHLC stats state ──────────────────────────────────────
  const [ohlcStats, setOhlcStats]       = useState(null);
  const [ohlcLoading, setOhlcLoading]   = useState(false);

  // ── Stable price formatter ────────────────────────────────
  const fmt = useCallback((amount) => {
    const n = parseFloat(amount) || 0;
    if (apiCurrency === 'NGN') {
      return new Intl.NumberFormat('en-NG', {
        style: 'currency', currency: 'NGN', minimumFractionDigits: 2,
      }).format(n);
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency', currency: 'USD', minimumFractionDigits: 2,
    }).format(n);
  }, [apiCurrency]);

  // ── Data fetching ─────────────────────────────────────────
  const fetchChart = useCallback(async () => {
    if (!symbol) return;
    try {
      setChartLoading(true);
      setChartError(null);
      const data = await chartService.getChartData(symbol, selectedInterval, 60);
      setChartData(data);
      if (data.display_currency) setApiCurrency(data.display_currency);
    } catch (err) {
      setChartError(err.message || 'Failed to load chart');
    } finally {
      setChartLoading(false);
    }
  }, [symbol, selectedInterval]);

  const fetchPrice = useCallback(async () => {
    if (!symbol) return;
    try {
      const price = await chartService.getCurrentPrice(symbol);
      setCurrentPrice(price);
      if (price.display_currency) setApiCurrency(price.display_currency);
    } catch (_) {}
  }, [symbol]);

  // ── OHLC stats fetching ───────────────────────────────────
  const fetchOhlcStats = useCallback(async () => {
    if (!symbol) return;
    try {
      setOhlcLoading(true);
      const data = await watchlistService.getOhlcStats(symbol, selectedInterval);
      setOhlcStats(data);
      if (data?.display_currency) setApiCurrency(data.display_currency);
    } catch (_) {
      setOhlcStats(null);
    } finally {
      setOhlcLoading(false);
    }
  }, [symbol, selectedInterval]);

  useEffect(() => {
    fetchChart();
    fetchPrice();
    fetchOhlcStats();
  }, [fetchChart, fetchPrice, fetchOhlcStats]);

  // ── Memoized Chart.js config ──────────────────────────────
  const config = useMemo(() => {
    if (!chartData?.data || chartData.data.length === 0) return null;

    const points = chartData.data;

    const labels = points.map((p) => {
      const d = new Date(p.timestamp);
      if (['1min', '5min', '15min', '1hr'].includes(selectedInterval)) {
        return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
      }
      if (selectedInterval === '24hr') {
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      }
      return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    });

    const prices = points.map(p => parseFloat(p.price));
    const barColors = prices.map((price, i) => {
      if (i === 0) return COLOR.green;
      const prev = prices[i - 1];
      const pct  = prev !== 0 ? ((price - prev) / prev) * 100 : 0;
      return resolveColor(pct);
    });

    return {
      labels,
      datasets: [{
        label: symbol,
        data: prices,
        backgroundColor: barColors,
        borderRadius: 3,
        borderSkipped: false,
      }],
    };
  }, [chartData, selectedInterval, symbol]);

  // ── Memoized Chart.js options ─────────────────────────────
  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: { label: (ctx) => fmt(ctx.parsed.y) },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { maxRotation: 0, maxTicksLimit: 8 },
      },
      y: {
        grid: { color: 'rgba(0,0,0,0.05)' },
        ticks: { callback: (v) => fmt(v) },
      },
    },
  }), [fmt]);

  // ── Remove from watchlist ─────────────────────────────────
  const handleToggleWatch = async () => {
    if (!watched || removing) return;
    setRemoving(true);
    try {
      await watchlistService.removeFromWatchlist(symbol);
      window.dispatchEvent(new CustomEvent('watchlistChange'));
      setWatched(false);
      setTimeout(() => navigate('/dashboard'), 800);
    } catch (_) {
      setRemoving(false);
    }
  };

  const priceChangePct = currentPrice?.price_change?.percent ?? 0;

  return (
    <div className="watchlist-chart-container">

      {/* ── Header ── */}
      <div className="watchlist-chart-header">
        <button className="wc-back-button" onClick={() => navigate(-1)}>
          ← Back
        </button>

        <div className="wc-stock-title">
          <h1>{symbol} — Price Chart</h1>
          {currentPrice && (
            <div className="wc-price-row">
              <span className="wc-current-price">{fmt(currentPrice.current_price)}</span>
              <span className={`wc-price-change ${priceChangePct >= 0 ? 'positive' : 'negative'}`}>
                {priceChangePct >= 0 ? '+' : ''}{priceChangePct.toFixed(2)}%
              </span>
            </div>
          )}
        </div>

        <button
          className={`wc-eye-button ${removing ? 'removing' : ''}`}
          onClick={handleToggleWatch}
          disabled={removing}
          title={watched ? 'Remove from watchlist' : 'Removed'}
        >
          {watched ? (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" fill="currentColor"/>
            </svg>
          ) : (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46A11.804 11.804 0 001 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z" fill="currentColor"/>
            </svg>
          )}
          {removing ? 'Removing...' : watched ? 'Watching' : 'Removed'}
        </button>
      </div>

      {/* ── Interval selector ── */}
      <div className="wc-interval-selector">
        {INTERVALS.map(iv => (
          <button
            key={iv.value}
            className={`wc-interval-btn ${selectedInterval === iv.value ? 'active' : ''}`}
            onClick={() => setSelectedInterval(iv.value)}
          >
            {iv.label}
          </button>
        ))}
      </div>

      {/* ── Chart + legend side by side (desktop) / stacked (mobile) ── */}
      <div className="wc-chart-legend-row">

        {/* Left: chart card */}
        <div className="wc-chart-card">
          <div className="wc-chart-title">
            {symbol} · Price movement ({selectedInterval})
          </div>

          {chartLoading ? (
            <div className="wc-loading">Loading chart data...</div>
          ) : chartError ? (
            <div className="wc-error">Error: {chartError}</div>
          ) : config ? (
            <div className="wc-bar-chart-wrapper">
              <Bar data={config} options={chartOptions} />
            </div>
          ) : (
            <div className="wc-no-data">No price data available for this interval.</div>
          )}
        </div>

        {/* Right: legend panel */}
        <div className="wc-legend-panel">
          <div className="wc-legend-title">Colour Guide</div>
          <div className="wc-legend">
            {LEGEND_ITEMS.map(({ colorKey, label }) => (
              <div key={colorKey} className="wc-legend-item">
                <span className={`wc-legend-dot wc-dot-${colorKey}`} />
                <span className="wc-legend-label">{label}</span>
              </div>
            ))}
          </div>
        </div>

      </div>

      {/* ── OHLC Stats Panel ── */}
      <div className="wc-stats-panel">
        <div className="wc-stats-header">
          <span className="wc-stats-title">Price Statistics</span>
          <span className="wc-stats-interval-badge">{selectedInterval}</span>
        </div>

        {ohlcLoading ? (
          <div className="wc-stats-loading">Loading statistics...</div>
        ) : (
          <div className="wc-stats-grid">
            {STAT_KEYS.map(({ key, label }) => {
              const displayVal = ohlcStats ? ohlcStats[`${key}_display`] : '—';
              return (
                <div key={key} className="wc-stat-tile">
                  <div className="wc-stat-left">
                    <div className="wc-stat-label">{label}</div>
                    <div className="wc-stat-value">
                      {ohlcStats ? displayVal : '—'}
                    </div>
                  </div>
                  <div className="wc-stat-visual">
                    {STAT_VISUAL[key]}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {ohlcStats && ohlcStats.data_points === 0 && (
          <p className="wc-stats-fallback-note">
            No price changes recorded in this interval — showing current price for all values.
          </p>
        )}
      </div>

    </div>
  );
};

export default WatchlistChart;